import { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { hapticSuccess } from "../../../../lib/haptics";
import { EmblemLoader } from "../assets/EmblemLoader";
import { stepTitleSx, textSecondary } from "./stepStyles";

/**
 * "Building your starting plan…" — a progress ring with a live percentage and
 * one personalized stage label at a time. Deliberately unhurried (~4.5s): the
 * plan really is assembled from the answers, and naming them back is the
 * honest version of the labor illusion. Back navigation is disabled while it
 * runs (the flow hides the header on this step).
 */

const TOTAL_MS = 4600;
const HOLD_MS = 300;
const RING_RADIUS = 54;

interface LoaderStepProps {
  stages: string[];
  onDone: () => void;
}

export const LoaderStep = ({ stages, onDone }: LoaderStepProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();
  const [stageIndex, setStageIndex] = useState(0);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const progress = useMotionValue(0);
  const percent = useTransform(progress, (value) => `${Math.round(value * 100)}%`);

  useEffect(() => {
    if (reduced) {
      progress.set(1);
      setStageIndex(stages.length - 1);
      doneTimer.current = setTimeout(onDone, 800);
      return () => {
        if (doneTimer.current) clearTimeout(doneTimer.current);
      };
    }
    const controls = animate(progress, 1, {
      duration: TOTAL_MS / 1000,
      ease: "linear",
      onComplete: () => {
        hapticSuccess();
        doneTimer.current = setTimeout(onDone, HOLD_MS);
      },
    });
    const stageInterval = setInterval(
      () => setStageIndex((index) => Math.min(index + 1, stages.length - 1)),
      TOTAL_MS / stages.length,
    );
    return () => {
      controls.stop();
      clearInterval(stageInterval);
      if (doneTimer.current) clearTimeout(doneTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        pb: 8,
        textAlign: "center",
      }}
    >
      <EmblemLoader size={56} />
      <Typography sx={stepTitleSx(isDark)}>Building your starting plan…</Typography>

      {/* Progress ring with a live percentage. */}
      <Box sx={{ position: "relative", width: 120, height: 120 }}>
        <svg viewBox="0 0 120 120" width="120" height="120">
          <circle
            cx="60"
            cy="60"
            r={RING_RADIUS}
            fill="none"
            stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}
            strokeWidth="4"
          />
          <motion.circle
            cx="60"
            cy="60"
            r={RING_RADIUS}
            fill="none"
            stroke="#22c55e"
            strokeWidth="4"
            strokeLinecap="round"
            transform="rotate(-90 60 60)"
            style={{ pathLength: progress }}
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <motion.span
            style={{
              fontSize: "1.75rem",
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              color: isDark ? "#ffffff" : "#1d1d1f",
            }}
          >
            {percent}
          </motion.span>
        </Box>
      </Box>

      {/* One stage label at a time. */}
      <Box sx={{ height: 24, display: "flex", alignItems: "center" }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={stageIndex}
            initial={reduced ? undefined : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: textSecondary(isDark) }}>
              {stages[stageIndex]}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};
